import type { PropsWithChildren } from "react";
import { checkTursoWebSupport } from "@-/dev.improve.haniverse.lib.database.engine/turso-sync";
import { useQuery } from "@-/com.npmjs.tanstack__react-query";
import { UnsupportedBrowserScreen } from "@-/dev.improve.haniverse.components.error.unsupported-browser";

const DATABASE_SUPPORT_QUERY_KEY = ["database-support"] as const;

/**
 * Gates the app on the storage backend it requires.
 *
 * On web the Turso wasm engine needs BOTH an OPFS sync access handle and a
 * cross-origin-isolated page (`SharedArrayBuffer`). Without the gate, a browser
 * missing either fails deep inside `loadSyncWasm()` with a stack trace that reads
 * like an app bug; with it, the user gets a screen naming the actual cause.
 *
 * On native `checkTursoWebSupport()` always resolves supported, so this is a
 * transparent passthrough (one cheap resolved promise, no UI flash worth guarding).
 */
export function DatabaseSupportGate({ children }: PropsWithChildren) {
  const { data: support } = useQuery({
    queryFn: checkTursoWebSupport,
    queryKey: DATABASE_SUPPORT_QUERY_KEY,
    staleTime: Infinity,
  });

  // Server rendering (web `EXPO_WEB_OUTPUT=server`) executes in Node, where the
  // probe — which needs a browser worker — can NEVER resolve, so `isPending` is
  // permanently true during SSR. If the pending branch returned a placeholder view,
  // `#root` would ship blank (every page's real content lives inside `children`) and
  // the app would paint white until the JS bundle hydrates — the exact "blank first
  // paint" this gate must not cause.
  //
  // So server and client both render `children` optimistically until the probe
  // POSITIVELY reports an unsupported environment. This keeps hydration markup
  // identical, avoids a blank second render while the query settles, and preserves
  // the nav provider tree's synchronous first-render contract.
  if (support?.supported === false) {
    return <UnsupportedBrowserScreen reason={support.reason} />;
  }

  return <>{children}</>;
}
